import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import AgencyAccessGuard from '../../components/gov/AgencyAccessGuard';
import api from '../../services/api';
import { 
  Plus, 
  FileText, 
  Users, 
  Settings, 
  TrendingUp,
  AlertCircle,
  Clock,
  CheckCircle,
  Building2,
  Award,
  Calendar,
  Briefcase,
  RotateCcw
} from 'lucide-react';
import VendorCard from '../../components/enterprise/VendorCard'
import { toast } from 'react-toastify';

const COMMON_CERTIFICATIONS = [
  '8(a)', 'HUBZone', 'WOSB', 'EDWOSB', 'VOSB', 'SDVOSB', 'MBE', 'DBE', 'SDB',
  'AbilityOne', 'GSA Schedule', 'SEWP', 'CIO-SP3'
]
const COMMON_AGENCIES = [
  'GSA', 'DOD', 'DOE', 'DHS', 'VA', 'NASA', 'EPA', 'USDA',
  'State of NY', 'State of CA', 'State of TX', 'State of FL',
  'NYC', 'LA County', 'Chicago'
]

interface DashboardStats {
  totalRfqs: number;
  activeRfqs: number;
  totalProposals: number;
  recentProposals: number;
  uniqueVendors: number;
}

interface RecentRfq {
  id: string;
  title: string;
  budgetRange: string;
  deadline: string;
  proposalCount: number;
  status: 'active' | 'expired' | 'draft';
}

const GovDashboard: React.FC = () => {
  const { user } = useAuthStore();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentRfqs, setRecentRfqs] = useState<RecentRfq[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'certifiedVendors'>('dashboard');
  const [certifiedVendors, setCertifiedVendors] = useState<any[]>([]);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [agency, setAgency] = useState<any>(null);
  const [agencyLoading, setAgencyLoading] = useState(true);
  const [agencyNameInput, setAgencyNameInput] = useState('');

  // Filter state for Certified Vendors tab
  const [minProjects, setMinProjects] = useState(0)
  const [minYear, setMinYear] = useState('')
  const [minCertDate, setMinCertDate] = useState('')
  const [govCertFilter, setGovCertFilter] = useState('')
  const [agencyFilter, setAgencyFilter] = useState('')

  // Filtered vendors based on selected filters
  const filteredVendors = useMemo(() => {
    return certifiedVendors.filter(vendor => {
      // Number of Projects Completed
      if (minProjects && (vendor.completedJobs || 0) < minProjects) return false
      // Type of Government Certification (NEW: strict to COMMON_CERTIFICATIONS filter)
      if (govCertFilter) {
        if (!Array.isArray(vendor.govCertifications) || !vendor.govCertifications.includes(govCertFilter)) return false
      }
      // Approved Government Agency (NEW)
      if (agencyFilter) {
        if (!Array.isArray(vendor.approvedAgencies) || !vendor.approvedAgencies.includes(agencyFilter)) return false
      }
      // Experience in Solar Industry (memberSince year)
      if (minYear) {
        const year = vendor.memberSince ? new Date(vendor.memberSince).getFullYear() : null
        if (!year || year > parseInt(minYear)) return false
      }
      // Date of Government Certification (certifiedSince or fallback to memberSince)
      if (minCertDate) {
        const certDate = vendor.certifiedSince || vendor.memberSince
        if (!certDate || new Date(certDate) < new Date(minCertDate)) return false
      }
      return true
    })
  }, [certifiedVendors, minProjects, minYear, minCertDate, govCertFilter, agencyFilter])

  // For year dropdown
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 40 }, (_, i) => (currentYear - i).toString());

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Fetch certified vendors when tab is selected
  useEffect(() => {
    if (activeTab === 'certifiedVendors') {
      setLoadingVendors(true);
      api.get('/api/enterprise/vendors?govCertifiedOnly=true&limit=1000').then((response: any) => {
        if (response?.success) {
          setCertifiedVendors(
            (response.data || []).filter(
              (v: any) => v.isGovCertified === true
            )
          );
        } else {
          setCertifiedVendors([]);
        }
      }).finally(() => setLoadingVendors(false));
    }
  }, [activeTab]);

  // Fetch agency info on mount
  useEffect(() => {
    const fetchAgency = async () => {
      setAgencyLoading(true);
      try {
        const res = await api.get('/api/gov/agency-status') as any;
        if (res?.success && res.data) {
          setAgency(res.data);
          setAgencyNameInput(res.data.agencyName || '');
        }
      } catch (err) {
        setAgency(null);
      } finally {
        setAgencyLoading(false);
      }
    };
    fetchAgency();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch dashboard statistics
      const [statsResponse, rfqsResponse] = await Promise.all([
        api.get('/api/gov/rfqs/stats'),
        api.get('/api/gov/rfqs/my-rfqs?limit=5')
      ]) as any;

      if (typeof statsResponse === 'object' && statsResponse && 'data' in statsResponse) {
        if ((statsResponse as any).data.success) {
          setStats((statsResponse as any).data.data);
        }
      }

      if (typeof rfqsResponse === 'object' && rfqsResponse && 'data' in rfqsResponse) {
        if ((rfqsResponse as any).data.success) {
          setRecentRfqs((rfqsResponse as any).data.data.rfqs.map((rfq: any) => ({
            id: rfq.id,
            title: rfq.title,
            budgetRange: rfq.budgetRange,
            deadline: rfq.deadline,
            proposalCount: rfq.GovProposals?.length || 0,
            status: new Date(rfq.deadline) > new Date() ? 'active' : 'expired'
          })));
        }
      }
    } catch (err: any) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'expired':
        return <Clock className="h-4 w-4 text-red-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  // Add local helpers for display names
  const getCertificationDisplayName = (cert: string): string => {
    const certMap: { [key: string]: string } = {
      '8(a)': 'SBA 8(a) Business Development',
      'HUBZone': 'Historically Underutilized Business Zone',
      'WOSB': 'Women-Owned Small Business',
      'EDWOSB': 'Economically Disadvantaged WOSB',
      'VOSB': 'Veteran-Owned Small Business',
      'SDVOSB': 'Service-Disabled Veteran-Owned Small Business',
      'MBE': 'Minority Business Enterprise',
      'DBE': 'Disadvantaged Business Enterprise',
      'SDB': 'Small Disadvantaged Business',
      'AbilityOne': 'AbilityOne Program',
      'GSA Schedule': 'GSA Schedule Contract',
      'SEWP': 'Solutions for Enterprise-Wide Procurement',
      'CIO-SP3': 'CIO-Solutions and Partners 3',
    }
    return certMap[cert] || cert
  }
  const getAgencyDisplayName = (agency: string): string => {
    const agencyMap: { [key: string]: string } = {
      'GSA': 'General Services Administration',
      'DOD': 'Department of Defense',
      'DOE': 'Department of Energy',
      'DHS': 'Department of Homeland Security',
      'VA': 'Department of Veterans Affairs',
      'NASA': 'National Aeronautics and Space Administration',
      'EPA': 'Environmental Protection Agency',
      'USDA': 'United States Department of Agriculture',
      'State of NY': 'New York State Government',
      'State of CA': 'California State Government',
      'State of TX': 'Texas State Government',
      'State of FL': 'Florida State Government',
      'NYC': 'New York City Government',
      'LA County': 'Los Angeles County Government',
      'Chicago': 'City of Chicago Government',
    }
    return agencyMap[agency] || agency
  }

  const handleSaveAgencyName = async () => {
    if (!agency?.agencyId) return;
    try {
      const res = await api.put(`/api/gov/agencies/${agency.agencyId}`, { name: agencyNameInput }) as any;
      if (res?.success && res.data) {
        setAgency({ ...agency, agencyName: res.data.name });
        setAgencyNameInput(res.data.name);
        toast.success('Agency name updated!');
      } else {
        toast.error('Failed to update agency name.');
      }
    } catch (err) {
      toast.error('Failed to update agency name.');
    }
  };

  if (loading) {
    return (
      <AgencyAccessGuard>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading dashboard...</p>
          </div>
        </div>
      </AgencyAccessGuard>
    );
  }

  return (
    <AgencyAccessGuard>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Government Agency Dashboard</h1>
                <p className="text-gray-600 mt-1 text-sm sm:text-base">
                  Welcome back, {user?.firstName}! Manage your RFQs and procurement activities.
                </p>
              </div>
              <Link
                to="/gov/rfqs/create"
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 w-full sm:w-auto"
              >
                <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="text-sm sm:text-base">Create New RFQ</span>
              </Link>
            </div>
          </div>

          {/* Tabs */}
          <div className="mb-8 border-b border-gray-200">
            <nav className="flex space-x-4 sm:space-x-8 overflow-x-auto">
              <button
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${activeTab === 'dashboard' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                onClick={() => setActiveTab('dashboard')}
              >
                Dashboard
              </button>
              <button
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 whitespace-nowrap ${activeTab === 'certifiedVendors' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                onClick={() => setActiveTab('certifiedVendors')}
              >
                <Users className="h-4 w-4" />
                <span>Certified Vendors</span>
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          {activeTab === 'dashboard' && (
            <>
              {/* Error State */}
              {error && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                    <p className="text-red-800">{error}</p>
                  </div>
                </div>
              )}

              {/* Stats Cards */}
              {stats && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 lg:gap-6 mb-8">
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
                    <div className="flex items-center">
                      <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                        <FileText className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
                      </div>
                      <div className="ml-3 sm:ml-4 flex-1 min-w-0">
                        <p className="text-xs sm:text-sm text-gray-600 truncate">Total RFQs</p>
                        <p className="text-xl sm:text-2xl font-semibold text-gray-900">{stats.totalRfqs}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
                    <div className="flex items-center">
                      <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
                        <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
                      </div>
                      <div className="ml-3 sm:ml-4 flex-1 min-w-0">
                        <p className="text-xs sm:text-sm text-gray-600 truncate">Active RFQs</p>
                        <p className="text-xl sm:text-2xl font-semibold text-gray-900">{stats.activeRfqs}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
                    <div className="flex items-center">
                      <div className="p-2 bg-purple-100 rounded-lg flex-shrink-0">
                        <Users className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600" />
                      </div>
                      <div className="ml-3 sm:ml-4 flex-1 min-w-0">
                        <p className="text-xs sm:text-sm text-gray-600 truncate">Total Proposals</p>
                        <p className="text-xl sm:text-2xl font-semibold text-gray-900">{stats.totalProposals}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
                    <div className="flex items-center">
                      <div className="p-2 bg-orange-100 rounded-lg flex-shrink-0">
                        <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6 text-orange-600" />
                      </div>
                      <div className="ml-3 sm:ml-4 flex-1 min-w-0">
                        <p className="text-xs sm:text-sm text-gray-600 truncate">Recent Proposals</p>
                        <p className="text-xl sm:text-2xl font-semibold text-gray-900">{stats.recentProposals}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
                    <div className="flex items-center">
                      <div className="p-2 bg-indigo-100 rounded-lg flex-shrink-0">
                        <Building2 className="h-5 w-5 sm:h-6 sm:w-6 text-indigo-600" />
                      </div>
                      <div className="ml-3 sm:ml-4 flex-1 min-w-0">
                        <p className="text-xs sm:text-sm text-gray-600 truncate">Unique Vendors</p>
                        <p className="text-xl sm:text-2xl font-semibold text-gray-900">{stats.uniqueVendors}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Main Content Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Recent RFQs */}
                <div className="lg:col-span-2">
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className="p-6 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <h2 className="text-xl font-semibold text-gray-900">Recent RFQs</h2>
                        <Link
                          to="/gov/rfqs"
                          className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                        >
                          View all
                        </Link>
                      </div>
                    </div>
                    
                    <div className="p-6">
                      {recentRfqs.length === 0 ? (
                        <div className="text-center py-8">
                          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                          <h3 className="text-lg font-medium text-gray-900 mb-2">No RFQs yet</h3>
                          <p className="text-gray-600 mb-4">
                            Start by creating your first Request for Quotation.
                          </p>
                          <Link
                            to="/gov/rfqs/create"
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            Create First RFQ
                          </Link>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {recentRfqs.map((rfq) => (
                            <div key={rfq.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <div className="flex-1">
                                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                                    <div className="flex items-center space-x-2">
                                    {getStatusIcon(rfq.status)}
                                      <h3 className="font-medium text-gray-900 text-sm sm:text-base">{rfq.title}</h3>
                                    </div>
                                    <span className={`px-2 py-1 text-xs rounded-full w-fit ${getStatusColor(rfq.status)}`}>
                                      {rfq.status}
                                    </span>
                                  </div>
                                  <div className="text-sm text-gray-600 space-y-1">
                                    <p><strong>Budget:</strong> {rfq.budgetRange}</p>
                                    <p><strong>Deadline:</strong> {formatDate(rfq.deadline)}</p>
                                    <p><strong>Proposals:</strong> {rfq.proposalCount}</p>
                                  </div>
                                </div>
                                <div className="flex-shrink-0">
                                  <Link
                                    to={`/gov/rfqs/${rfq.id}`}
                                    className="text-blue-600 hover:text-blue-700 text-sm font-medium whitespace-nowrap"
                                  >
                                    View Details
                                  </Link>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="space-y-6">
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className="p-6 border-b border-gray-200">
                      <h2 className="text-xl font-semibold text-gray-900">Quick Actions</h2>
                    </div>
                    <div className="p-6">
                      <div className="space-y-4">
                        <Link
                          to="/gov/rfqs/create"
                          className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors touch-manipulation"
                        >
                          <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                            <Plus className="h-5 w-5 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900">Create New RFQ</p>
                            <p className="text-sm text-gray-600">Start a new procurement request</p>
                          </div>
                        </Link>

                        <Link
                          to="/gov/rfqs"
                          className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors touch-manipulation"
                        >
                          <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
                            <FileText className="h-5 w-5 text-green-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900">Manage RFQs</p>
                            <p className="text-sm text-gray-600">View and edit existing RFQs</p>
                          </div>
                        </Link>

                        <Link
                          to="/gov/proposals"
                          className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors touch-manipulation"
                        >
                          <div className="p-2 bg-purple-100 rounded-lg flex-shrink-0">
                            <Users className="h-5 w-5 text-purple-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900">Review Proposals</p>
                            <p className="text-sm text-gray-600">Evaluate vendor submissions</p>
                          </div>
                        </Link>

                        <Link
                          to="/gov/settings"
                          className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors touch-manipulation"
                        >
                          <div className="p-2 bg-gray-100 rounded-lg flex-shrink-0">
                            <Settings className="h-5 w-5 text-gray-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900">Agency Settings</p>
                            <p className="text-sm text-gray-600">Configure agency preferences</p>
                          </div>
                        </Link>
                      </div>
                    </div>
                  </div>

                  {/* Agency Info */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className="p-6 border-b border-gray-200">
                      <h2 className="text-xl font-semibold text-gray-900">Agency Information</h2>
                    </div>
                    <div className="p-6">
                      {agencyLoading ? (
                        <div className="text-gray-500 text-sm">Loading agency info...</div>
                      ) : !agency ? (
                        <div className="text-red-500 text-sm">Unable to load agency info.</div>
                      ) : (
                        <div className="space-y-3">
                          <div>
                            <p className="text-sm text-gray-600">Agency Name</p>
                            <input
                              type="text"
                              className="font-medium text-gray-900 border border-gray-300 rounded px-2 py-1 w-full"
                              value={agencyNameInput}
                              onChange={e => setAgencyNameInput(e.target.value)}
                            />
                            <button
                              className="mt-2 px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs"
                              onClick={handleSaveAgencyName}
                              type="button"
                              disabled={agencyLoading || !agencyNameInput.trim()}
                            >
                              Save
                            </button>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Contact Email</p>
                            <p className="font-medium text-gray-900">{agency.agencyEmail}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Account Status</p>
                            <div className="flex items-center space-x-2">
                              <CheckCircle className="h-4 w-4 text-green-500" />
                              <span className="text-sm font-medium text-green-800">{agency.isApproved ? 'Verified' : 'Pending'}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
          {activeTab === 'certifiedVendors' && (
            <div className="py-6">
              {/* Elegant Filter Panel */}
              <div className="bg-white rounded-lg shadow p-4 mb-6">
                <div className="space-y-4">
                  {/* Mobile Layout - Stacked */}
                  <div className="block sm:hidden space-y-4">
                    {/* Min Projects Slider */}
                    <div className="flex flex-col items-start">
                      <label className="flex items-center text-xs font-medium text-gray-700 mb-1 gap-1">
                        <Briefcase className="w-4 h-4 text-gray-400" /> Min Projects
                      </label>
                      <input
                        type="range"
                        min={0}
                        max={50}
                        step={1}
                        value={minProjects}
                        onChange={e => setMinProjects(Number(e.target.value))}
                        className="w-full"
                      />
                      <span className="text-xs text-gray-500 mt-1">{minProjects}+</span>
                    </div>
                    {/* Gov Certification Type (NEW) */}
                    <div className="flex flex-col items-start">
                      <label className="flex items-center text-xs font-medium text-gray-700 mb-1 gap-1">
                        <Award className="w-4 h-4 text-gray-400" /> Gov Certification
                      </label>
                      <select
                        value={govCertFilter}
                        onChange={e => setGovCertFilter(e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded"
                      >
                        <option value="">All Types</option>
                        {COMMON_CERTIFICATIONS.map(cert => (
                          <option key={cert} value={cert}>{getCertificationDisplayName(cert)}</option>
                        ))}
                      </select>
                    </div>
                    {/* Approved Government Agency (NEW) */}
                    <div className="flex flex-col items-start">
                      <label className="flex items-center text-xs font-medium text-gray-700 mb-1 gap-1">
                        <Building2 className="w-4 h-4 text-gray-400" /> Approved Agency
                      </label>
                      <select
                        value={agencyFilter}
                        onChange={e => setAgencyFilter(e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded"
                      >
                        <option value="">All Agencies</option>
                        {COMMON_AGENCIES.map(agency => (
                          <option key={agency} value={agency}>{getAgencyDisplayName(agency)}</option>
                        ))}
                      </select>
                    </div>
                    {/* Experience Year Dropdown */}
                    <div className="flex flex-col items-start">
                      <label className="flex items-center text-xs font-medium text-gray-700 mb-1 gap-1">
                        <Users className="w-4 h-4 text-gray-400" /> Since
                      </label>
                      <select
                        value={minYear}
                        onChange={e => setMinYear(e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded"
                      >
                        <option value="">Any Year</option>
                        {years.map(y => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                    </div>
                    {/* Certified Since Date */}
                    <div className="flex flex-col items-start">
                      <label className="flex items-center text-xs font-medium text-gray-700 mb-1 gap-1">
                        <Calendar className="w-4 h-4 text-gray-400" /> Certified Since
                      </label>
                      <input
                        type="date"
                        value={minCertDate}
                        onChange={e => setMinCertDate(e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded"
                      />
                    </div>
                    {/* Reset Button */}
                    <button
                      type="button"
                      className="w-full flex items-center justify-center gap-1 px-3 py-2 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => { setMinProjects(0); setMinYear(''); setMinCertDate(''); setGovCertFilter(''); setAgencyFilter(''); }}
                    >
                      <RotateCcw className="w-4 h-4" /> Reset All Filters
                    </button>
                  </div>

                  {/* Desktop Layout - Grid */}
                  <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 items-end">
                {/* Min Projects Slider */}
                    <div className="flex flex-col items-start">
                  <label className="flex items-center text-xs font-medium text-gray-700 mb-1 gap-1">
                    <Briefcase className="w-4 h-4 text-gray-400" /> Min Projects
                  </label>
                  <input
                    type="range"
                    min={0}
                    max={50}
                    step={1}
                    value={minProjects}
                    onChange={e => setMinProjects(Number(e.target.value))}
                        className="w-full"
                  />
                  <span className="text-xs text-gray-500 mt-1">{minProjects}+</span>
                </div>
                {/* Gov Certification Type (NEW) */}
                    <div className="flex flex-col items-start">
                  <label className="flex items-center text-xs font-medium text-gray-700 mb-1 gap-1">
                    <Award className="w-4 h-4 text-gray-400" /> Gov Certification
                  </label>
                  <select
                    value={govCertFilter}
                    onChange={e => setGovCertFilter(e.target.value)}
                    className="w-full px-2 py-1 border border-gray-300 rounded"
                  >
                    <option value="">All Types</option>
                    {COMMON_CERTIFICATIONS.map(cert => (
                      <option key={cert} value={cert}>{getCertificationDisplayName(cert)}</option>
                    ))}
                  </select>
                </div>
                {/* Approved Government Agency (NEW) */}
                    <div className="flex flex-col items-start">
                  <label className="flex items-center text-xs font-medium text-gray-700 mb-1 gap-1">
                    <Building2 className="w-4 h-4 text-gray-400" /> Approved Agency
                  </label>
                  <select
                    value={agencyFilter}
                    onChange={e => setAgencyFilter(e.target.value)}
                    className="w-full px-2 py-1 border border-gray-300 rounded"
                  >
                    <option value="">All Agencies</option>
                    {COMMON_AGENCIES.map(agency => (
                      <option key={agency} value={agency}>{getAgencyDisplayName(agency)}</option>
                    ))}
                  </select>
                </div>
                {/* Experience Year Dropdown */}
                    <div className="flex flex-col items-start">
                  <label className="flex items-center text-xs font-medium text-gray-700 mb-1 gap-1">
                    <Users className="w-4 h-4 text-gray-400" /> Since
                  </label>
                  <select
                    value={minYear}
                    onChange={e => setMinYear(e.target.value)}
                    className="w-full px-2 py-1 border border-gray-300 rounded"
                  >
                    <option value="">Any Year</option>
                    {years.map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
                {/* Certified Since Date */}
                    <div className="flex flex-col items-start">
                  <label className="flex items-center text-xs font-medium text-gray-700 mb-1 gap-1">
                    <Calendar className="w-4 h-4 text-gray-400" /> Certified Since
                  </label>
                  <input
                    type="date"
                    value={minCertDate}
                    onChange={e => setMinCertDate(e.target.value)}
                    className="w-full px-2 py-1 border border-gray-300 rounded"
                  />
                </div>
                {/* Reset Button */}
                <button
                  type="button"
                      className="flex items-center gap-1 px-3 py-1 border border-gray-300 rounded text-xs text-gray-700 hover:bg-gray-100"
                  onClick={() => { setMinProjects(0); setMinYear(''); setMinCertDate(''); setGovCertFilter(''); setAgencyFilter(''); }}
                >
                  <RotateCcw className="w-4 h-4" /> Reset
                </button>
                  </div>
                </div>
              </div>
              {/* Vendor Grid */}
              {loadingVendors ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Loading certified vendors...</p>
                </div>
              ) : filteredVendors.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-600">No certified vendors found.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredVendors.map((vendor) => (
                    <VendorCard
                      key={vendor.id}
                      vendor={vendor}
                      onViewDetails={(vendorId) => {
                        window.location.href = `/gov/vendor/${vendorId}`
                      }}
                      onRequestQuote={undefined}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </AgencyAccessGuard>
  );
};

export default GovDashboard; 